import type { ScaleTime } from 'd3-scale';

import type { TimeseriesDatapoint } from '../../../../core/types/timeseries.js';
import { isSymlogScale } from '../../../../core/utils/detect-scale.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { YAxisScales } from '../../../types/state.js';
import { DatapointsMap, WeakMapDatapoints } from '../../../types/timeseries.js';
import { getYScaleByUnit } from '../../../utils/yAxis.js';

const TIMESTAMP_BAR_WIDTH = 5;
const TIMESTAMP_BAR_X_DEVIATION = 2;

const getYCoordinates = (
  { value = 0, cumulativeValue = 0, unit }: DatapointsMap,
  scale: YAxisScales,
) => {
  const yScale = getYScaleByUnit(unit, scale);

  const valueCoordinate = yScale(value);
  const zeroCoordinate = yScale(0);
  const cumulativeCoordinate = yScale(cumulativeValue);
  const baseCumulative = yScale(cumulativeValue - value);

  const maxValue = yScale.invert(0);
  const maxValueCoordinate = yScale(maxValue);

  return {
    yScale,
    valueCoordinate,
    zeroCoordinate,
    cumulativeCoordinate,
    baseCumulative,
    maxValueCoordinate,
  };
};

/**
 * Gets bar position on X-axis.
 *
 * @param xScale - The Scale of the timeframe
 * @param start - The starting timestamp of the bar
 * @param end - The ending timestamp of the bar
 * @returns x position
 */
export function getBarXPosition(
  { end, start }: DatapointsMap,
  xScale: ScaleTime<number, number>,
): number {
  const isTimeframe = end && end !== start;

  return xScale(start) - (isTimeframe ? 0 : TIMESTAMP_BAR_X_DEVIATION);
}

/**
 * Gets bar position on Y-axis.
 *
 * @param dataPoint - Current data point
 * @param yScale - Scale of the y axis
 * @returns The Y position
 */
export function getBarYPosition(
  dataPointMetadata: DatapointsMap,
  yScale: YAxisScales,
): number {
  if (!yScale) {
    return 0;
  }
  const { valueCoordinate, zeroCoordinate, cumulativeCoordinate } =
    getYCoordinates(dataPointMetadata, yScale);
  let y = cumulativeCoordinate;

  if ((dataPointMetadata.value ?? 0) < 0) {
    const height = Math.abs(valueCoordinate - zeroCoordinate);
    y -= height;
  }

  return y;
}

/**
 * Gets bar height.
 *
 * @param dataPoint - Current data point
 * @param yScale - Scale of the y axis
 * @returns The bar height
 */
export function getBarHeight(
  dataPointMetadata: DatapointsMap,
  yScale: YAxisScales,
): number {
  const {
    valueCoordinate,
    zeroCoordinate,
    cumulativeCoordinate,
    baseCumulative,
    yScale: scale,
  } = getYCoordinates(dataPointMetadata, yScale);

  let height = zeroCoordinate - valueCoordinate;

  const isLogValue =
    isSymlogScale(scale) &&
    dataPointMetadata.cumulativeValue !== dataPointMetadata.value;

  if (isLogValue) {
    height = baseCumulative - cumulativeCoordinate;
  }

  return height;
}

/**
 * Gets bar width using the xScale for time frames or the default for timestamps.
 *
 * @param xScale - The Scale of the timeframe
 * @param start - The starting timestamp of the bar
 * @param end - The ending timestamp of the bar
 * @returns The bar width
 */
export function getBarWidth(
  { end, start }: DatapointsMap,
  xScale: ScaleTime<number, number>,
): number {
  const isTimeframe = end && end !== start;
  const width = isTimeframe ? xScale(end) - xScale(start) : TIMESTAMP_BAR_WIDTH;

  return width;
}

/**
 * Get curry accessor of the bar-height
 *
 * @returns getBarHeight
 */
export function height(datapointsMap: WeakMapDatapoints, yScale: YAxisScales) {
  return (dataPoint: TimeseriesDatapoint) => {
    const dataPointMetadata = safeGet(datapointsMap, dataPoint);

    return getBarHeight(dataPointMetadata, yScale);
  };
}

/**
 * Get curry accessor of the bar-width
 *
 * @returns getBarWidth
 */
export function width(
  datapointsMap: WeakMapDatapoints,
  xScale: ScaleTime<number, number>,
) {
  return (dataPoint: TimeseriesDatapoint) => {
    const dataPointMetadata = safeGet(datapointsMap, dataPoint);

    return getBarWidth(dataPointMetadata, xScale);
  };
}

/**
 * Get curry accessor of the bar-y-position
 *
 * @returns getBarYPosition
 */
export function y(datapointsMap: WeakMapDatapoints, yScale: YAxisScales) {
  return (dataPoint: TimeseriesDatapoint) => {
    const dataPointMetadata = safeGet(datapointsMap, dataPoint);

    const { cumulativeCoordinate, maxValueCoordinate } = getYCoordinates(
      dataPointMetadata,
      yScale,
    );

    const yCoordinate = maxValueCoordinate + cumulativeCoordinate;

    return yCoordinate;
  };
}

/**
 * Get curry accessor of the bar-x-position
 *
 * @returns getBarXPosition
 */
export function x(
  datapointsMap: WeakMapDatapoints,
  xScale: ScaleTime<number, number>,
) {
  return (dataPoint: TimeseriesDatapoint) => {
    const dataPointMetadata = safeGet(datapointsMap, dataPoint);

    return getBarXPosition(dataPointMetadata, xScale);
  };
}
